/**
 * Centralized API Client
 *
 * Provides a configured axios instance with:
 * - Automatic base URL injection
 * - Automatic authentication token injection
 * - Centralized error handling
 * - Request/response interceptors for logging
 * - Consistent error responses
 */

import axios from 'axios';
import axiosRetry from 'axios-retry';

/**
 * Create axios instance with base configuration
 * All API calls use relative paths (e.g. /api/user) — the proxy layer
 * (Vite dev server or Nginx) routes them to the backend.
 */
const apiClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Configure automatic retry logic
 * - Retries network errors and 5xx server errors
 * - Uses exponential backoff (1s, 2s, 4s)
 * - Max 3 retry attempts
 */
axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry on network errors
    if (axiosRetry.isNetworkError(error)) {
      return true;
    }

    // Retry on specific HTTP status codes
    if (error.response) {
      const status = error.response.status;
      // Retry on 5xx server errors, 408 timeout, and 429 rate limit
      return status >= 500 || status === 408 || status === 429;
    }

    return false;
  },
  onRetry: (retryCount, error, requestConfig) => {
    // Log retry attempts in development
    if (import.meta.env.DEV) {
      console.log(
        `[API Retry] Attempt ${retryCount}/3 for ${requestConfig.method.toUpperCase()} ${requestConfig.url}`,
        error.message
      );
    }
  },
});

/**
 * Request Interceptor
 * Automatically inject auth token from localStorage
 */
apiClient.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');

    if (token) {
      config.headers['x-auth-token'] = token;
    }

    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Session-expiry callback. The AuthProvider registers a handler here so the
 * (non-React) client can tell the app to log out + show "session expired" when
 * a refresh ultimately fails. Kept as a simple registry since the client lives
 * outside the React tree.
 */
let onSessionExpired = null;
/**
 * Register (or clear with null) the session-expiry handler.
 * @param {(() => void) | null} handler
 */
export function setOnSessionExpired(handler) {
  onSessionExpired = handler;
}

// Single-flight refresh: concurrent 401s share one in-flight refresh promise so
// we never fire a stampede of /auth/refresh calls.
let refreshPromise = null;

/** True for the auth endpoints themselves — never try to refresh those. */
function isAuthPath(url = '') {
  return (
    url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')
  );
}

/**
 * Exchange the stored refresh token for a new access+refresh pair.
 * Resolves to the new access token, or null if refresh isn't possible.
 * Uses a bare axios call to avoid recursing through this interceptor.
 */
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    const res = await axios.post('/api/auth/refresh', { refresh_token: refreshToken });
    const { token, refresh_token: newRefresh } = res.data;
    localStorage.setItem('authToken', token);
    if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
    return token;
  } catch {
    return null;
  }
}

/**
 * Response Interceptor
 * Handle errors consistently across the app
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      // Handle blob responses differently to avoid logging issues
      const dataForLog =
        response.data instanceof Blob
          ? `Blob[${response.data.type}] (${response.data.size} bytes)`
          : response.data;
      console.log(`[API Response] ${response.status} ${response.config.url}`, dataForLog);
    }

    return response;
  },
  async (error) => {
    // --- Refresh-on-401 ---------------------------------------------------
    // A 401 on a normal request likely means the short-lived access token
    // expired. Try to silently refresh once and replay the original request.
    // Skip the /auth/* endpoints (they manage tokens themselves) and any
    // request we've already retried (avoid loops).
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !isAuthPath(original.url)
    ) {
      original._retried = true;
      // Single-flight: first 401 starts the refresh; concurrent ones await it.
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = original.headers || {};
        original.headers['x-auth-token'] = newToken;
        return apiClient(original); // replay with the fresh token
      }
      // Refresh failed -> hard session expiry. Clear tokens and notify the app.
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      if (onSessionExpired) onSessionExpired();
    }

    // Extract error details
    const errorResponse = {
      message: 'An unexpected error occurred',
      status: null,
      data: null,
    };

    if (error.response) {
      // Server responded with error status
      errorResponse.status = error.response.status;
      errorResponse.data = error.response.data;

      // Extract error message from response
      // FastAPI uses 'detail' (string or array), other backends may use 'message'
      const detail = error.response.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors: [{msg: "...", loc: [...]}]
        errorResponse.message = detail.map((e) => e.msg).join('; ');
      } else if (typeof detail === 'string') {
        errorResponse.message = detail;
      } else {
        errorResponse.message =
          error.response.data?.message || error.response.statusText || errorResponse.message;
      }

      // Log error in development
      if (import.meta.env.DEV) {
        console.error(`[API Error] ${error.response.status} ${error.config.url}`, {
          message: errorResponse.message,
          data: error.response.data,
        });
      }

      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - token expired or invalid
          errorResponse.message = 'Authentication required. Please log in again.';
          // Optional: Trigger logout or redirect to login
          // This could be handled by a global error handler
          break;
        case 403:
          errorResponse.message = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorResponse.message = 'The requested resource was not found.';
          break;
        case 500:
          errorResponse.message = 'Server error. Please try again later.';
          break;
      }
    } else if (error.request) {
      // Request made but no response received (network error)
      errorResponse.message = 'Network error. Please check your connection.';

      if (import.meta.env.DEV) {
        console.error('[API Network Error]', error.message);
      }
    } else {
      // Something else happened
      errorResponse.message = error.message || errorResponse.message;

      if (import.meta.env.DEV) {
        console.error('[API Error]', error);
      }
    }

    // Attach formatted error to the error object
    error.apiError = errorResponse;

    return Promise.reject(error);
  }
);

/**
 * Convenience methods for common HTTP operations
 */
export const api = {
  /**
   * GET request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config (params, headers, etc.)
   * @returns {Promise} Response data
   */
  get: (url, config = {}) => apiClient.get(url, config).then((res) => res.data),

  /**
   * POST request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} Response data
   */
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config).then((res) => res.data),

  /**
   * PUT request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} Response data
   */
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config).then((res) => res.data),

  /**
   * PATCH request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {object} config - Axios config
   * @returns {Promise} Response data
   */
  patch: (url, data = {}, config = {}) =>
    apiClient.patch(url, data, config).then((res) => res.data),

  /**
   * DELETE request
   * @param {string} url - API endpoint
   * @param {object} config - Axios config
   * @returns {Promise} Response data
   */
  delete: (url, config = {}) => apiClient.delete(url, config).then((res) => res.data),
};

/**
 * Export axios instance for advanced usage
 * (e.g., file uploads with custom headers)
 */
export default apiClient;
